import * as React from 'react';
import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { IconButton, Switch, Paper, Tooltip } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { sendServerRequest } from '../../../server/request';
import { openGlobalSnack } from '../../utils/snack';
import { CircularLoading } from '../../utils/loading';

const ENDPOINT = '/server/cltCommand/providerOrder';

type ProviderEntry = { name: string; enabled: boolean };

// Rótulos amigáveis por provider.
const LABELS: Record<string, string> = {
  v8: 'V8',
  suma: 'Suma',
  // A Soma entra como um provider por bancalizadora (nomes vindos do servidor).
  suma_celcoin: 'Suma · Celcoin',
  suma_uy3: 'Suma · UY3',
};

function label(name: string) {
  return LABELS[name] ?? name.toUpperCase();
}

export function ProviderOrderPanel() {
  const [order, setOrder] = useState<ProviderEntry[]>([]);
  const [serverSnapshot, setServerSnapshot] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  function load() {
    setLoading(true);
    sendServerRequest('GET', `${ENDPOINT}/get`)
      .then((e) => {
        const list: ProviderEntry[] = e.data?.data?.order ?? [];
        setOrder(list);
        setServerSnapshot(JSON.stringify(list));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar a ordem dos provedores.', 'error');
      });
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
  }

  function toggle(index: number, value: boolean) {
    const next = [...order];
    next[index] = { ...next[index], enabled: value };
    setOrder(next);
  }

  function save() {
    setSaving(true);
    sendServerRequest('POST', `${ENDPOINT}/set`, { order })
      .then((e) => {
        const list: ProviderEntry[] = e.data?.data?.order ?? order;
        setOrder(list);
        setServerSnapshot(JSON.stringify(list));
        setSaving(false);
        openGlobalSnack('Ordem dos provedores atualizada.', 'success');
      })
      .catch(() => {
        setSaving(false);
        openGlobalSnack('Não foi possível salvar a ordem.', 'error');
      });
  }

  const dirty = JSON.stringify(order) !== serverSnapshot;

  if (loading) return <CircularLoading />;

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
      <h3 style={{ marginBottom: 4 }}>Ordem de consulta dos provedores</h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0, marginBottom: 16 }}>
        A consulta de margem tenta os provedores habilitados nesta ordem (de cima para baixo).
        Desabilitados são ignorados.
      </p>

      {order.map((p, i) => (
        <Paper
          key={p.name}
          variant="outlined"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            marginBottom: 8,
            opacity: p.enabled ? 1 : 0.55,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 600, minWidth: 24 }}>{i + 1}º</span>
            <span>{label(p.name)}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Tooltip title={p.enabled ? 'Habilitado' : 'Desabilitado'}>
              <Switch
                checked={p.enabled}
                onChange={(e) => toggle(i, e.target.checked)}
              />
            </Tooltip>
            <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={i === order.length - 1}
              onClick={() => move(i, 1)}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </div>
        </Paper>
      ))}

      <div style={{ marginTop: 16 }}>
        <LoadingButton
          loading={saving}
          disabled={!dirty}
          variant="contained"
          onClick={save}
        >
          Salvar ordem
        </LoadingButton>
      </div>
    </div>
  );
}
